import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { FormikHelpers } from 'formik';
import { message } from 'antd';
import { useBackgroundNavigate } from '@shared/hooks';
import { localURLMaker } from '@shared/utils';
import { CashRegisterOperationsService } from '../../services';

export type CashRegisterOperationFormValues = {
  name: string;
  parentId: string;
};

export const useCashRegisterOperationForm = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useBackgroundNavigate();

  const detail = useQuery(
    ['cash-register-operation', id],
    async () => {
      const result = await CashRegisterOperationsService.getById(id!);
      if (result.status === 200) return result.data;
      throw new Error(result.data as string);
    },
    { enabled: !!id, cacheTime: 0 },
  );

  const initialValues = useMemo<CashRegisterOperationFormValues>(() => {
    if (id && detail.data) {
      return { name: detail.data.name, parentId: detail.data.parent ? String(detail.data.parent.id) : '' };
    }
    return { name: '', parentId: '' };
  }, [id, detail.data]);

  const onSubmit = useCallback(
    async (values: CashRegisterOperationFormValues, helpers: FormikHelpers<CashRegisterOperationFormValues>) => {
      const result = await CashRegisterOperationsService.create({ id, name: values.name, parentId: values.parentId || '' });
      if (result.status === 200) {
        message.success(id ? 'Dəyişikliklər saxlanıldı.' : 'Kateqoriya yaradıldı.');
        navigate(localURLMaker('/cash-flow/operations', {}, { reFetchCashRegisterOperationsTable: '1' }));
      } else if (result.status === 422) {
        const e = result.data as Record<string, string>;
        helpers.setErrors({ name: e.category_name, parentId: e.parent_id });
      } else {
        message.error(result.data as string);
      }
      helpers.setSubmitting(false);
    },
    [id, navigate],
  );

  return { initialValues, onSubmit, id, isLoading: !!id && (detail.isLoading || !detail.data) };
};
